import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Countdown } from "./Countdown";
import { WebinarFuelWidget } from "./WebinarFuelWidget";
import { Reveal, PartnerBadge, StruckPrice } from "./shared";

const METRICS = [
  { label: "Signal Coverage", value: "50+ Behavioral Signals" },
  { label: "API Level", value: "Read-Only SP-API · Zero Write Risk" },
  { label: "Live Demo", value: "Real Account · Split Screen" },
];

export const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden">
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(60rem 30rem at 75% -10%, rgba(99,102,241,0.09), transparent), radial-gradient(40rem 24rem at -10% 30%, rgba(79,70,229,0.05), transparent)",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-16 lg:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
      <div className="lg:col-span-7 space-y-7">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.2em] font-semibold text-indigo-700">
            Free live workshop · Account health secrets Amazon won't show you
          </p>
          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-[#0b1528] mt-5"
          >
            Your Account Health page isn't live. Learn to catch issues{" "}
            <span className="text-indigo-500 italic underline decoration-[#f97066] decoration-[5px] underline-offset-[10px]">
              before Amazon does.
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mt-6">
            Seller Central only updates after violations are already logged. In this
            free live workshop, we reveal the secrets and tricks to watch your real
            account health signals in real time — and avoid suspensions before they
            ever hit.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="grid sm:grid-cols-3 gap-3 max-w-2xl">
            {METRICS.map((m) => (
              <div key={m.label} className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{m.label}</p>
                <p className="font-mono text-[13px] font-semibold text-slate-900 mt-1 leading-snug">{m.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-7">
            <PartnerBadge />
          </div>
        </Reveal>
      </div>

      <div id="register" className="lg:col-span-5 scroll-mt-24">
        <Reveal delay={0.15} className="relative">
          <motion.div
            aria-hidden="true"
            className="hidden lg:flex absolute -left-32 top-36 items-center gap-2 -rotate-6 z-10"
            animate={{ x: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          >
            <span className="font-mono text-xs font-bold text-indigo-600 uppercase tracking-widest">
              Start here
            </span>
            <ArrowRight className="w-7 h-7 text-indigo-600" strokeWidth={2.5} />
          </motion.div>
          <div
            data-testid="registration-card"
            className="bg-white rounded-2xl border border-slate-200/90 shadow-xl shadow-slate-200/60 p-6 sm:p-8"
          >
            <div className="text-center mb-6">
              <p className="font-display font-extrabold text-[#0b1528] text-3xl sm:text-4xl">
                Usually <StruckPrice>$997</StruckPrice>{" "}
                <span className="text-emerald-600">FREE</span>
              </p>
              <p className="font-display font-bold text-[#0b1528] text-lg mt-1">NO Payment Required</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-3.5 py-1.5">
                Live account health workshop
              </span>
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
                Free
              </span>
            </div>
            <div className="mt-6">
              <Countdown />
            </div>
            <div className="mt-6 border-t border-slate-100 pt-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <motion.span
                  aria-hidden="true"
                  animate={{ y: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.3, ease: "easeInOut" }}
                >
                  <ArrowDown className="w-4 h-4 text-indigo-600" strokeWidth={2.5} />
                </motion.span>
                <span className="font-mono text-[11px] uppercase tracking-widest font-semibold text-indigo-700">
                  Pick your session time below
                </span>
                <motion.span
                  aria-hidden="true"
                  animate={{ y: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.3, ease: "easeInOut", delay: 0.2 }}
                >
                  <ArrowDown className="w-4 h-4 text-indigo-600" strokeWidth={2.5} />
                </motion.span>
              </div>
              <WebinarFuelWidget />
            </div>
            <p className="font-mono text-[10px] text-slate-500 text-center mt-5 tracking-wide">
              100% Free · No pitch fest · Direct case-study teardowns
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
